/** The landing page: every project the caller can see. */

import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import { Badge, Empty, ErrorState, Field, Loading, Notice, RelativeTime } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";
import { useSession } from "@/hooks/useSession";

/** Turn a name into the slug the API would accept, so the field can be filled in for them. */
function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function NewProject({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [isPii, setIsPii] = useState(true);
  const [retentionDays, setRetentionDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  const [fields, setFields] = useState<Record<string, string>>({});

  if (!open) {
    return (
      <button type="button" className="button button--primary" onClick={() => setOpen(true)}>
        New project
      </button>
    );
  }

  const setNameAndSlug = (value: string) => {
    setName(value);
    if (!slugEdited) setSlug(toSlug(value));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    setFields({});
    try {
      await endpoints.createProject({
        slug,
        name,
        description: description || null,
        is_pii: isPii,
        artifact_retention_days: retentionDays,
      });
      setOpen(false);
      setName("");
      setSlug("");
      setDescription("");
      onCreated();
    } catch (caught) {
      if (caught instanceof ApiError && Object.keys(caught.fields).length > 0) {
        setFields(
          Object.fromEntries(
            Object.entries(caught.fields).map(([key, messages]) => [key, messages.join(" ")]),
          ),
        );
      } else {
        setProblem((caught as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <div className="card__header">
        <span className="card__title">New project</span>
      </div>

      <div className="card__body stack" style={{ gap: 14 }}>
        {problem && <Notice tone="danger">{problem}</Notice>}

        <div className="row" style={{ gap: 12, alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Field label="Name" error={fields.name} required htmlFor="project-name">
              <input
                id="project-name"
                className="input"
                value={name}
                required
                placeholder="Acme Speech 2026"
                onChange={(event) => setNameAndSlug(event.target.value)}
              />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Field
              label="Slug"
              hint="Used in URLs and in the storage path. Cannot be changed later."
              error={fields.slug}
              required
              htmlFor="project-slug"
            >
              <input
                id="project-slug"
                className="input mono"
                value={slug}
                required
                onChange={(event) => {
                  setSlugEdited(true);
                  setSlug(event.target.value);
                }}
              />
            </Field>
          </div>
        </div>

        <Field label="Description" htmlFor="project-description">
          <input
            id="project-description"
            className="input"
            value={description}
            placeholder="What this project delivers, and for whom."
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <div className="checkbox-row">
          <input
            id="project-pii"
            type="checkbox"
            checked={isPii}
            onChange={(event) => setIsPii(event.target.checked)}
          />
          <label htmlFor="project-pii">
            <span className="field__label">This project holds customer data</span>
            <div className="field__hint">
              Every download then asks for a reason, recorded against the person who asked.
              Leave it on unless you are certain otherwise.
            </div>
          </label>
        </div>

        <Field
          label="Artifact retention"
          hint="How long output files are kept. The record of each run outlives them."
          error={fields.artifact_retention_days}
          htmlFor="project-retention"
        >
          <div className="row" style={{ gap: 8 }}>
            <input
              id="project-retention"
              className="input"
              type="number"
              min={1}
              max={3650}
              style={{ width: 120 }}
              value={retentionDays}
              onChange={(event) => setRetentionDays(Number(event.target.value))}
            />
            <span className="muted">days</span>
          </div>
        </Field>

        <div className="row">
          <button type="submit" className="button button--primary" disabled={busy}>
            {busy ? "Creating…" : "Create project"}
          </button>
          <button type="button" className="button button--ghost" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

export function Projects() {
  const { me } = useSession();
  const { data, error, loading, reload } = useAsync(() => endpoints.projects({ limit: 100 }), []);

  if (loading) return <Loading rows={4} label="Loading projects" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const projects = data?.items ?? [];

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Projects</h1>
          <div className="page-header__meta">
            {projects.length === 1 ? "1 project" : `${projects.length} projects`}
          </div>
        </div>
      </div>

      <div className="stack">
        {me?.is_platform_admin && <NewProject onCreated={reload} />}

        {projects.length === 0 ? (
          <div className="card">
            <Empty title="No projects yet">
              {me?.is_platform_admin ? (
                <>
                  Create one to get started. A project is the unit of access, storage, and
                  notification.
                </>
              ) : (
                <>
                  You have an account but no role on any project yet. Ask a project
                  administrator to give you one.
                </>
              )}
            </Empty>
          </div>
        ) : (
          <div className="grid-cards">
            {projects.map((project) => (
              <Link key={project.id} to={`/projects/${project.slug}`} className="card">
                <div className="card__body">
                  <div className="row row--between" style={{ marginBottom: 6 }}>
                    <h2 className="truncate">{project.name}</h2>
                    {project.my_role && <Badge tone="accent">{project.my_role}</Badge>}
                  </div>

                  <div className="muted" style={{ minHeight: 38, fontSize: 13 }}>
                    {project.description ?? <span className="faint">No description</span>}
                  </div>

                  <div className="row" style={{ marginTop: 12, gap: 12, fontSize: 12.5 }}>
                    <span className="muted">
                      {project.script_count} {project.script_count === 1 ? "script" : "scripts"}
                    </span>
                    {project.is_pii && <Badge tone="warning">Customer data</Badge>}
                    {project.archived_at && <Badge>Archived</Badge>}
                  </div>

                  <div className="faint" style={{ marginTop: 10, fontSize: 12 }}>
                    Created <RelativeTime iso={project.created_at} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
